import { useState, useEffect } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";

interface DateSelectorProps {
  value: string | null;
  onChange: (date: string | null) => void;
  minYear?: number;
  maxYear?: number;
  disabled?: boolean;
  label?: string;
  hideDay?: boolean;
}

const MONTHS = [
  { value: "01", label: "Janeiro" },
  { value: "02", label: "Fevereiro" },
  { value: "03", label: "Março" },
  { value: "04", label: "Abril" },
  { value: "05", label: "Maio" },
  { value: "06", label: "Junho" },
  { value: "07", label: "Julho" },
  { value: "08", label: "Agosto" },
  { value: "09", label: "Setembro" },
  { value: "10", label: "Outubro" },
  { value: "11", label: "Novembro" },
  { value: "12", label: "Dezembro" },
];

export function DateSelector({
  value,
  onChange,
  minYear = 1920,
  maxYear = new Date().getFullYear(),
  disabled = false,
  label,
  hideDay = false,
}: DateSelectorProps) {
  const years = Array.from(
    { length: maxYear - minYear + 1 },
    (_, i) => (maxYear - i).toString()
  );
  const days = Array.from({ length: 31 }, (_, i) => (i + 1).toString().padStart(2, "0"));

  // Parse existing value
  const parseDate = (dateStr: string | null) => {
    if (!dateStr) return { day: "", month: "", year: "" };
    const [year, month, day] = dateStr.split("-");
    return { day: day || "", month: month || "", year: year || "" };
  };

  // Use local state to preserve partial selections
  const [localDay, setLocalDay] = useState("");
  const [localMonth, setLocalMonth] = useState("");
  const [localYear, setLocalYear] = useState("");

  // Sync with external value
  useEffect(() => {
    const { day, month, year } = parseDate(value);
    setLocalDay(day);
    setLocalMonth(month);
    setLocalYear(year);
  }, [value]);

  const handleDayChange = (newDay: string) => {
    setLocalDay(newDay);
    if (localYear && localMonth) {
      onChange(`${localYear}-${localMonth}-${newDay}`);
    }
  };

  const handleMonthChange = (newMonth: string) => {
    setLocalMonth(newMonth);
    if (localYear && newMonth) {
      const finalDay = hideDay ? "01" : (localDay || "01");
      onChange(`${localYear}-${newMonth}-${finalDay}`);
    }
  };

  const handleYearChange = (newYear: string) => {
    setLocalYear(newYear);
    if (newYear && localMonth) {
      const finalDay = hideDay ? "01" : (localDay || "01");
      onChange(`${newYear}-${localMonth}-${finalDay}`);
    }
  };

  return (
    <div className="space-y-2">
      {label && <Label>{label}</Label>}
      <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2">
        {!hideDay && (
          <Select value={localDay || undefined} onValueChange={handleDayChange} disabled={disabled}>
            <SelectTrigger className="w-full sm:w-[80px]">
              <SelectValue placeholder="Dia" />
            </SelectTrigger>
            <SelectContent>
              {days.map((d) => (
                <SelectItem key={d} value={d}>
                  {d}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <Select value={localMonth || undefined} onValueChange={handleMonthChange} disabled={disabled}>
          <SelectTrigger className="w-full sm:flex-1">
            <SelectValue placeholder="Mês" />
          </SelectTrigger>
          <SelectContent>
            {MONTHS.map((m) => (
              <SelectItem key={m.value} value={m.value}>
                {m.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={localYear || undefined} onValueChange={handleYearChange} disabled={disabled}>
          <SelectTrigger className="w-full sm:w-[100px]">
            <SelectValue placeholder="Ano" />
          </SelectTrigger>
          <SelectContent>
            {years.map((y) => (
              <SelectItem key={y} value={y}>
                {y}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}
